import React from 'react';
import { Tooltip } from '@mui/material';
import { SafetyRoute } from '@common/domain/types';
import './ClassificationBadge.css';

export type { SafetyRoute };

interface ClassificationBadgeProps {
    safetyRoute?: SafetyRoute;
    route?: SafetyRoute;
    compact?: boolean;
}

// Icons
const ChatBubbleIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
);

const BookIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" strokeLinejoin="round" strokeLinecap="round" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
);

const ShieldIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
);

const ZapIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
);

const WarningIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeLinejoin="round" strokeLinecap="round" />
        <line x1="12" y1="9" x2="12" y2="13" strokeLinecap="round" />
        <line x1="12" y1="17" x2="12.01" y2="17" strokeLinecap="round" />
    </svg>
);

const categoryConfig = {
    normal: {
        label: 'Normal',
        icon: ShieldIcon,
        className: 'badge-normal',
    },
    caution: {
        label: 'Caution',
        icon: WarningIcon,
        className: 'badge-caution',
    },
    harmful: {
        label: 'Harmful',
        icon: WarningIcon,
        className: 'badge-harmful',
    },
};

const intentConfig = {
    small_talk: {
        label: 'Small Talk',
        icon: ChatBubbleIcon,
    },
    deep_talk: {
        label: 'Deep Talk',
        icon: BookIcon,
    },
    straight_talk: {
        label: 'Straight Talk',
        icon: ZapIcon,
    },
};

export const ClassificationBadge: React.FC<ClassificationBadgeProps> = (props) => {
    const safetyRoute = props.safetyRoute ?? props.route;
    const compact = props.compact;
    if (!safetyRoute) return null;

    const category = categoryConfig[safetyRoute?.category] ?? categoryConfig.normal;
    const intent = intentConfig[safetyRoute?.intent] ?? intentConfig.small_talk;
    const CategoryIcon = category.icon;
    const IntentIcon = intent.icon;
    const reasonText = safetyRoute.reason && String(safetyRoute.reason).toUpperCase() !== 'NONE'
        ? `${category.label} | ${safetyRoute.reason}`
        : category.label;

    return (
        <div className={`classification-badge-container ${compact ? 'compact' : ''}`}>
            {/* Safety Category Badge */}
            <Tooltip title={reasonText} placement="top">
                <div className={`classification-badge ${category.className}`}>
                    <CategoryIcon />
                    {!compact && <span>{category.label}</span>}
                </div>
            </Tooltip>

            {/* Intent Badge */}
            <Tooltip title={intent.label} placement="top">
                <div className="classification-badge badge-intent">
                    <IntentIcon />
                    {!compact && <span>{intent.label}</span>}
                </div>
            </Tooltip>
        </div>
    );
};

export default ClassificationBadge;
